import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { PlanType } from '../config/stripe';
import { apiUrl } from '../config/api';
import { supabase } from '../config/supabase';

interface StripeSubscription {
  id: string;
  status: string;
  items?: { data: Array<{ id: string; price: { id: string } }> };
  pause_collection?: { behavior: string } | null;
  cancel_at_period_end?: boolean;
  current_period_end?: number; // unix timestamp (seconds)
}

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const { user, signOut, resetPassword } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [statusType, setStatusType] = useState<'success' | 'error' | ''>('');
  const [subscription, setSubscription] = useState<StripeSubscription | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<PlanType>('monthly');
  const [subscriptionId, setSubscriptionId] = useState<string>('');
  const [nextBillingDate, setNextBillingDate] = useState<string | null>(null);
  const [profileDob, setProfileDob] = useState<string>('');
  const [profileCountry, setProfileCountry] = useState<string>('');
  const [profileState, setProfileState] = useState<string>('');

  useEffect(() => {
    // Load any previously stored values
    const storedId = localStorage.getItem('subscriptionId') || '';
    if (storedId) setSubscriptionId(storedId);
    const storedPlan = (localStorage.getItem('subscriptionPlan') as PlanType | null) || null;
    if (storedPlan) {
      setSelectedPlan(storedPlan);
    }
  }, []);

  const inferPlanFromPriceId = (priceId?: string | null): PlanType | null => {
    if (!priceId) return null;
    // Compare with env-driven price IDs when available
    const monthlyId = process.env.REACT_APP_STRIPE_MONTHLY_PRICE_ID_TEST || process.env.REACT_APP_STRIPE_MONTHLY_PRICE_ID_LIVE;
    const yearlyId = process.env.REACT_APP_STRIPE_YEARLY_PRICE_ID_TEST || process.env.REACT_APP_STRIPE_YEARLY_PRICE_ID_LIVE;
    if (monthlyId && priceId === monthlyId) return 'monthly';
    if (yearlyId && priceId === yearlyId) return 'yearly';
    return null;
  };

  const fetchSubscription = useCallback(async () => {
    if (!subscriptionId) return;
    try {
      const response = await fetch(apiUrl(`/api/subscription/${subscriptionId}`));
      if (!response.ok) {
        throw new Error('Failed to retrieve subscription');
      }
      const json = await response.json();
      setSubscription(json);
      const inferred = inferPlanFromPriceId(json.items?.data?.[0]?.price?.id);
      if (inferred) {
        setSelectedPlan(inferred);
      }
      // Derive next billing date immediately from subscription if available
      if (json?.current_period_end) {
        setNextBillingDate(new Date(json.current_period_end * 1000).toLocaleDateString());
      } else {
        // Fallback: ask backend helper
        try {
          const res = await fetch(apiUrl(`/api/subscription-next-date/${subscriptionId}`));
          if (res.ok) {
            const data = await res.json();
            if (data?.nextBillingDate) {
              setNextBillingDate(new Date(data.nextBillingDate * 1000).toLocaleDateString());
            }
          }
        } catch {}
      }
    } catch (error) {
      setSubscription(null);
      setStatusType('error');
      setStatusMessage('Could not load subscription details.');
    }
  }, [subscriptionId]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Load profile fields from Supabase metadata with localStorage fallback
  useEffect(() => {
    const loadProfileFields = async () => {
      // localStorage fallback
      const lsDob = localStorage.getItem('userProfile.dateOfBirth') || '';
      const lsCountry = localStorage.getItem('userProfile.country') || '';
      const lsState = localStorage.getItem('userProfile.state') || '';

      try {
        const { data } = await supabase.auth.getUser();
        const metadata: any = data?.user?.user_metadata || {};
        setProfileDob((metadata?.dateOfBirth as string) || lsDob);
        setProfileCountry((metadata?.country as string) || lsCountry);
        setProfileState((metadata?.state as string) || lsState);
      } catch {
        setProfileDob(lsDob);
        setProfileCountry(lsCountry);
        setProfileState(lsState);
      }
    };
    loadProfileFields();
  }, []);

  // If we have no local subscription id but we do have a logged-in email,
  // attempt to discover the latest active/trialing subscription from the backend.
  useEffect(() => {
    const syncByEmail = async () => {
      if (subscriptionId || !user?.email) return;
      try {
        const res = await fetch(apiUrl(`/api/subscription-by-email?email=${encodeURIComponent(user.email)}`));
        if (!res.ok) return; // silently ignore if not found
        const json: StripeSubscription = await res.json();
        setSubscriptionId(json.id);
        try {
          localStorage.setItem('subscriptionId', json.id);
        } catch {}
        // Fetch summary next
        fetchSubscription();
      } catch {
        // ignore
      }
    };
    syncByEmail();
  }, [subscriptionId, user?.email, fetchSubscription]);

  // Keep next billing date in sync when subscription changes
  useEffect(() => {
    if (subscription?.current_period_end) {
      setNextBillingDate(new Date(subscription.current_period_end * 1000).toLocaleDateString());
    }
  }, [subscription?.current_period_end]);

  const handleResetPassword = async () => {
    if (!user?.email) return;
    setIsProcessing(true);
    setStatusMessage('');
    setStatusType('');
    try {
      const { error } = await resetPassword(user.email);
      if (error) {
        throw error;
      }
      setStatusType('success');
      setStatusMessage('Password reset email sent. Check your inbox.');
    } catch (err: any) {
      setStatusType('error');
      setStatusMessage(err?.message || 'Failed to send password reset.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSignOut = async () => {
    setIsProcessing(true);
    try {
      const { error } = await signOut();
      if (error) throw error;
      navigate('/');
    } catch (err) {
      setStatusType('error');
      setStatusMessage('Failed to log out.');
    } finally {
      setIsProcessing(false);
    }
  };

  // plan switching and pause/resume removed from UI per requirements

  const cancelSubscription = async () => {
    if (!subscriptionId) return;
    setIsProcessing(true);
    setStatusMessage('');
    setStatusType('');
    try {
      const res = await fetch(apiUrl(`/api/cancel-subscription/${subscriptionId}`), { method: 'POST' });
      if (!res.ok) throw new Error('Failed to cancel subscription');
      const json = await res.json();
      setSubscription(json);
      setStatusType('success');
      setStatusMessage('Subscription set to cancel at period end.');
    } catch (err: any) {
      setStatusType('error');
      setStatusMessage(err?.message || 'Could not cancel subscription.');
    } finally {
      setIsProcessing(false);
    }
  };

  const resumeSubscription = async () => {
    if (!subscriptionId) return;
    setIsProcessing(true);
    setStatusMessage('');
    setStatusType('');
    try {
      const res = await fetch(apiUrl(`/api/resume-subscription/${subscriptionId}`), { method: 'POST' });
      if (!res.ok) throw new Error('Failed to resume subscription');
      const json = await res.json();
      setSubscription(json);
      setStatusType('success');
      setStatusMessage('Subscription will continue at period end.');
    } catch (err: any) {
      setStatusType('error');
      setStatusMessage(err?.message || 'Could not resume subscription.');
    } finally {
      setIsProcessing(false);
    }
  };

  const isPaused = !!subscription?.pause_collection;
  const isCanceling = !!subscription?.cancel_at_period_end;
  const currentPlan: PlanType | null = inferPlanFromPriceId(subscription?.items?.data?.[0]?.price?.id) || selectedPlan || null;

  return (
    <div className="container" style={{ padding: '24px 0' }}>
      <h2>Profile</h2>

      {statusMessage && (
        <div style={{
          margin: '12px 0',
          padding: '12px',
          borderRadius: 6,
          background: statusType === 'success' ? '#e6ffed' : '#ffecec',
          color: statusType === 'success' ? '#0a7a1f' : '#a10000'
        }}>
          {statusMessage}
        </div>
      )}

      <div style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 8 }}><strong>Email</strong></div>
        <div>{user?.email}</div>
        {(profileDob || profileCountry || profileState) && (
          <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
            {profileDob && (
              <div>
                <div style={{ marginBottom: 4 }}><strong>Date of Birth</strong></div>
                <div>{profileDob}</div>
              </div>
            )}
            {profileCountry && (
              <div>
                <div style={{ marginBottom: 4 }}><strong>Country</strong></div>
                <div>{profileCountry}</div>
              </div>
            )}
            {profileCountry === 'US' && profileState && (
              <div>
                <div style={{ marginBottom: 4 }}><strong>State</strong></div>
                <div>{profileState}</div>
              </div>
            )}
          </div>
        )}
        <div style={{ marginTop: 12 }}>
          <button onClick={handleResetPassword} disabled={isProcessing} className="btn btn-secondary" style={{ marginRight: 8 }}>
            Request Password Reset
          </button>
          <button onClick={handleSignOut} disabled={isProcessing} className="btn btn-secondary">
            Log Out
          </button>
        </div>
      </div>

      <hr />

      <div style={{ marginTop: 24 }}>
        <h3>Subscription</h3>
        {!subscriptionId && (
          <div>
            <p>No subscription found.</p>
            <button className="btn btn-primary" onClick={() => navigate('/onboarding')}>Subscribe</button>
          </div>
        )}

        {subscriptionId && (
          <div>
            <div style={{ marginBottom: 12 }}>
              <div><strong>Plan</strong>: {currentPlan ? `${currentPlan === 'monthly' ? 'Monthly' : 'Yearly'}` : 'Unknown'}</div>
              {nextBillingDate && (
                <div><strong>Next billing date</strong>: {nextBillingDate}</div>
              )}
              {isPaused && <div><strong>Paused</strong>: Yes</div>}
              {isCanceling && <div><strong>Cancel at period end</strong>: Yes</div>}
            </div>

            <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {isCanceling ? (
                <button className="btn btn-secondary" onClick={resumeSubscription} disabled={isProcessing}>
                  Resume Subscription
                </button>
              ) : (
                <button className="btn btn-secondary" onClick={cancelSubscription} disabled={isProcessing}>
                  Cancel Subscription
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;


